import React, { useState, useEffect } from 'react';
import { Plus, Search, Calendar } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.js';
import { eventsService } from '../../services/firestoreService.js';
import { EventCard } from '../Dashboard/EventCard.js';
import { CreateEventModal } from '../Dashboard/CreateEventModal.js';
import EditEventModal from '../Dashboard/EditEventModal.js';

export const Events = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Cargar eventos del organizador
  useEffect(() => {
    const loadEvents = async () => {
      if (!user?.uid) return;
      
      try {
        setLoading(true);
        const organizerEvents = await eventsService.getOrganizerEvents(user.uid);
        setEvents(organizerEvents);
      } catch (error) {
        console.error('Error loading events:', error);
        // En caso de error, usar datos mock como fallback
        setEvents([
          {
            eventId: 1,
            name: 'Fiesta de 15 - María González',
            status: 'active',
            date: '2024-09-15',
            location: 'Salón Principal',
            participants: 150,
            photos: 89,
            joinCode: 'MARIA15'
          },
          {
            eventId: 2,
            name: 'Casamiento - Juan y Ana',
            status: 'inactive',
            date: '2024-09-20',
            location: 'Jardín Botánico',
            participants: 200,
            photos: 0,
            joinCode: 'JUANANA'
          },
          {
            eventId: 3,
            name: 'Recital - Banda Local',
            status: 'inactive',
            date: '2024-08-30',
            location: 'Teatro Municipal',
            participants: 300,
            photos: 156,
            joinCode: 'BANDA2024'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [user?.uid]);

  // Recargar eventos después de crear uno nuevo
  const handleEventCreated = async () => {
    if (!user?.uid) return;
    
    try {
      const organizerEvents = await eventsService.getOrganizerEvents(user.uid);
      setEvents(organizerEvents);
    } catch (error) {
      console.error('Error reloading events after creation:', error);
    }
  };

  // Recargar eventos después de editar uno
  const handleEventUpdated = async () => {
    if (!user?.uid) return;
    
    try {
      const organizerEvents = await eventsService.getOrganizerEvents(user.uid);
      setEvents(organizerEvents);
    } catch (error) {
      console.error('Error reloading events after update:', error);
    }
  };

  // Manejar la edición de un evento
  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setIsEditModalOpen(true);
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedEvent(null);
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase());
    const effective = event.status === 'active' ? 'active' : 'inactive';
    const matchesStatus = statusFilter === 'all' || effective === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusCount = (status) => {
    if (status === 'active') return events.filter(e => e.status === 'active').length;
    if (status === 'inactive') return events.filter(e => e.status !== 'active').length;
    return events.length;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Gestión de Eventos
        </h1>
        <p className="text-gray-600">
          Crea, gestiona y monitorea todos tus eventos en un solo lugar
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Eventos</p>
              <p className="text-2xl font-bold text-gray-900">{events.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Calendar className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Activos</p>
              <p className="text-2xl font-bold text-gray-900">{getStatusCount('active')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Calendar className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Inactivos</p>
              <p className="text-2xl font-bold text-gray-900">{getStatusCount('inactive')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between flex-wrap gap-4">
          <div className="flex flex-1 flex-col sm:flex-row gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar eventos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {/* Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-auto"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
          {/* Create Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center self-start md:self-auto whitespace-nowrap"
          >
            <Plus className="h-5 w-5 mr-2" />
            Crear Evento
          </button>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-2 gap-6">
        {filteredEvents.map((event) => (
          <EventCard 
            key={event.eventId} 
            event={event} 
            onEdit={handleEditEvent}
            onUpdated={handleEventUpdated}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron eventos</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' 
              ? 'Intenta ajustar los filtros de búsqueda.'
              : 'Comienza creando tu primer evento.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <div className="mt-6">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors"
              >
                Crear Evento
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEventCreated={handleEventCreated}
      />

      {/* Edit Event Modal */}
      <EditEventModal
        event={selectedEvent}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onEventUpdated={handleEventUpdated}
      />
    </div>
  );
};
